import { useState } from "react";

import { useEnvironment } from "../../config/environment-context.js";
import { Button } from "../../shared/ui/button.js";
import {
  eligibleFilteredLiveBots,
  type FilteredLiveAction,
  useApplyFilteredLiveAction,
} from "./filtered-live-queries.js";
import type { BotView } from "./models.js";

export function FilteredLiveControls({ bots }: { readonly bots: readonly BotView[] }) {
  const environment = useEnvironment();
  const [open, setOpen] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const applyAction = useApplyFilteredLiveAction();
  const readyBots = eligibleFilteredLiveBots(bots, "ready");
  const startBots = eligibleFilteredLiveBots(bots, "start");
  const stopBots = eligibleFilteredLiveBots(bots, "stop");
  const unassignedCount = bots.filter(
    (bot) =>
      (bot.state === "ready" || bot.state === "stopped") && bot.liveBrokerAccountId === undefined,
  ).length;
  const succeededCount =
    applyAction.data?.filter((outcome) => outcome.status === "succeeded").length ?? 0;
  const failed = applyAction.data?.filter((outcome) => outcome.status === "failed") ?? [];
  const pendingAction = applyAction.variables?.action;

  const submit = (action: FilteredLiveAction) => {
    const eligible = action === "ready" ? readyBots : action === "start" ? startBots : stopBots;
    applyAction.mutate({ action, bots: eligible }, { onSuccess: () => setConfirmed(false) });
  };

  return (
    <div className="filtered-live-launcher">
      <Button
        disabled={bots.length === 0 || applyAction.isPending}
        onClick={() => {
          applyAction.reset();
          setConfirmed(false);
          setOpen((current) => !current);
        }}
        tone="quiet"
      >
        {open ? "Close live controls" : "Ready/start/stop filtered"}
      </Button>
      {open ? (
        <form
          aria-label="Prepare, start, or stop live for filtered bots"
          className="form-stack filtered-live-form"
          onSubmit={(event) => event.preventDefault()}
        >
          <div>
            <p className="eyebrow">Real orders</p>
            <h2>Filtered lifecycle controls</h2>
            <p className="section-description">
              Make {readyBots.length} ready, start {startBots.length}, or stop {stopBots.length}{" "}
              eligible bots currently shown. Draft and error bots can be made ready. Starting
              preserves each bot's assigned Tastytrade account.
              {unassignedCount === 0
                ? ""
                : ` ${String(unassignedCount)} start-eligible ${unassignedCount === 1 ? "bot is" : "bots are"} excluded because no live account is assigned.`}
            </p>
          </div>
          <label className="check-field">
            <input
              checked={confirmed}
              onChange={(event) => setConfirmed(event.currentTarget.checked)}
              type="checkbox"
            />
            <span>
              <strong>I understand starting and stopping affect live trading</strong>
              <small>
                Making ready does not trade. Starting can submit real orders; stopping changes
                lifecycle after broker reconciliation.
              </small>
            </span>
          </label>
          {!environment.VITE_ENABLE_LIVE_TRADING && startBots.length > 0 ? (
            <p className="field-hint">
              Starting live bots is disabled by this deployment. Stopping remains available.
            </p>
          ) : null}
          {applyAction.error === null ? null : (
            <p className="form-error" role="alert">
              The bulk action stopped unexpectedly. No additional bots will be submitted.
            </p>
          )}
          {applyAction.data === undefined ? null : (
            <div className="bulk-launch-result" role="status">
              <strong>
                Updated {succeededCount} of {applyAction.data.length} bots.
              </strong>
              {failed.length === 0 ? null : (
                <p>
                  Failed: {failed.map((outcome) => outcome.botName).join(", ")}. Refresh or open
                  each bot to review its current lifecycle state.
                </p>
              )}
            </div>
          )}
          <div className="button-row">
            <Button
              disabled={readyBots.length === 0 || applyAction.isPending}
              onClick={() => submit("ready")}
              tone="quiet"
              type="button"
            >
              {applyAction.isPending && pendingAction === "ready"
                ? "Making ready…"
                : `Make ${String(readyBots.length)} ${readyBots.length === 1 ? "bot" : "bots"} ready`}
            </Button>
            <Button
              disabled={
                !confirmed ||
                startBots.length === 0 ||
                applyAction.isPending ||
                !environment.VITE_ENABLE_LIVE_TRADING
              }
              onClick={() => submit("start")}
              title={
                environment.VITE_ENABLE_LIVE_TRADING
                  ? undefined
                  : "Live trading is disabled for this deployment."
              }
              type="button"
            >
              {applyAction.isPending && pendingAction === "start"
                ? "Starting…"
                : `Start ${String(startBots.length)} ${startBots.length === 1 ? "bot" : "bots"}`}
            </Button>
            <Button
              disabled={!confirmed || stopBots.length === 0 || applyAction.isPending}
              onClick={() => submit("stop")}
              tone="danger"
              type="button"
            >
              {applyAction.isPending && pendingAction === "stop"
                ? "Stopping…"
                : `Stop ${String(stopBots.length)} ${stopBots.length === 1 ? "bot" : "bots"}`}
            </Button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
